import { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { hasRequiredRole } from '../../utils/auth/rbac';

/**
 * Custom hook for managing authentication state and user roles
 * @returns {Object} Authentication and user related data and functions
 */
const useAuth = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const getUserInfo = async () => {
      try {
        if (authState?.isAuthenticated) {
          const info = await oktaAuth.getUser();
          if (isMounted) {
            setUserInfo(info);
            setError(null);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to get user information');
          console.error('User info error:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    getUserInfo();

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  const hasRole = (requiredRoles) => {
    if (!userInfo?.groups) return false;
    return hasRequiredRole(
      userInfo.groups,
      Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles]
    );
  };

  const logout = async (options = {}) => {
    try {
      await oktaAuth.signOut(options);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  };

  return {
    isAuthenticated: authState?.isAuthenticated || false,
    isLoading: loading || authState?.isPending,
    user: userInfo,
    error,
    hasRole,
    logout,
    oktaAuth,
  };
};

export default useAuth;
